import apiClient from './apiClient';

const API_URL = '/affiliate';

export interface AffiliateStats {
  dealId: string;
  clickCount: number;
  conversionCount: number;
  conversionRate: number;
  totalConversionValue: number;
}

export interface ConversionData {
  clickId: string;
  purchaseValue: number;
  orderId?: string;
  productId?: string;
  categoryId?: string;
  affiliatePartner: string;
  commissionRate: number;
}

export interface RevenueReport {
  totalClicks: number;
  totalConversions: number;
  conversionRate: number;
  totalRevenue: number;
  totalCommissions: number;
  averageOrderValue: number;
  topPerformingDeals: any[];
  revenueByPartner: any[];
  monthlyTrends: any[];
}

export interface UserAffiliateStats {
  totalDealsSubmitted: number;
  totalClicks: number;
  totalConversions: number;
  totalCommissionsEarned: number;
  pendingCommissions: number;
  topPerformingDeals: any[];
  commissionHistory: any[];
}

export interface Commission {
  _id: string;
  dealID: any;
  clickID: string;
  submitterID: any;
  affiliatePartner: string;
  commissionRate: number;
  purchaseValue: number;
  commissionAmount: number;
  currency: string;
  status: 'pending' | 'confirmed' | 'paid' | 'cancelled';
  payoutDate?: string;
  conversionTimestamp: string;
  metadata: {
    orderId?: string;
    productId?: string;
    categoryId?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CommissionsResponse {
  commissions: Commission[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

/**
 * Get the affiliate redirect URL for a deal
 * This doesn't perform a redirect, just returns the URL to use
 */
export const getAffiliateRedirectUrl = (dealId: string): string => {
  return `/api${API_URL}/redirect/${dealId}`;
};

/**
 * Get affiliate statistics for a deal (admin only)
 */
export const getAffiliateStats = async (dealId: string): Promise<AffiliateStats> => {
  try {
    const response = await apiClient.get(`${API_URL}/stats/${dealId}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching affiliate stats:', error);
    throw error;
  }
};

/**
 * Record a conversion for a deal (admin only)
 */
export const recordConversion = async (
  dealId: string,
  clickId: string,
  value?: number
): Promise<any> => {
  try {
    const response = await apiClient.post(`${API_URL}/conversion/${dealId}`, {
      clickId,
      value,
    });
    return response.data.data;
  } catch (error) {
    console.error('Error recording conversion:', error);
    throw error;
  }
};

/**
 * Record advanced conversion with commission tracking (admin only)
 */
export const recordAdvancedConversion = async (conversionData: ConversionData): Promise<any> => {
  try {
    const response = await apiClient.post(`${API_URL}/advanced-conversion`, conversionData);
    return response.data.data;
  } catch (error) {
    console.error('Error recording advanced conversion:', error);
    throw error;
  }
};

/**
 * Get comprehensive revenue report (admin only)
 */
export const getRevenueReport = async (
  startDate?: string,
  endDate?: string
): Promise<RevenueReport> => {
  try {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await apiClient.get(`${API_URL}/revenue-report`, { params });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching revenue report:', error);
    throw error;
  }
};

/**
 * Get user affiliate statistics
 */
export const getUserAffiliateStats = async (userId: string): Promise<UserAffiliateStats> => {
  try {
    const response = await apiClient.get(`${API_URL}/user-stats/${userId}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching user affiliate stats:', error);
    throw error;
  }
};

/**
 * Update commission status (admin only)
 */
export const updateCommissionStatus = async (
  commissionId: string,
  status: 'pending' | 'confirmed' | 'paid' | 'cancelled',
  payoutDate?: string
): Promise<Commission> => {
  try {
    const response = await apiClient.put(`${API_URL}/commission/${commissionId}/status`, {
      status,
      payoutDate,
    });
    return response.data.data;
  } catch (error) {
    console.error('Error updating commission status:', error);
    throw error;
  }
};

/**
 * Get affiliate partner performance (admin only)
 */
export const getPartnerPerformance = async (
  partner: string,
  startDate?: string,
  endDate?: string
): Promise<any> => {
  try {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await apiClient.get(`${API_URL}/partner-performance/${partner}`, { params });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching partner performance:', error);
    throw error;
  }
};

/**
 * Get all commissions with pagination and filtering (admin only)
 */
export const getCommissions = async (
  page: number = 1,
  limit: number = 20,
  filters?: {
    status?: string;
    submitterId?: string;
    startDate?: string;
    endDate?: string;
  }
): Promise<CommissionsResponse> => {
  try {
    const params: any = { page, limit };
    if (filters) {
      Object.assign(params, filters);
    }

    const response = await apiClient.get(`${API_URL}/commissions`, { params });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching commissions:', error);
    throw error;
  }
};
